import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { Pill, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { HIPAABadge } from "@/components/HIPAACompliance";

/**
 * Public landing page that commits a patient's email change once they click
 * the verification link sent to their NEW inbox. No auth required — the token
 * itself authorizes the change and is single-use with a 2h TTL.
 */
export default function VerifyEmailChange() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("This link is missing its verification token.");
      return;
    }
    API.get(`/profile/email-change/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setStatus("success");
        setNewEmail(data.new_email || "");
      })
      .catch((e) => {
        setStatus("error");
        setMessage(e.response?.data?.detail || "We couldn't verify this link.");
      });
  }, [params]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="verify-email-change-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
            <Pill className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-2xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </span>
        </Link>
        <div className="flex justify-center mb-4"><HIPAABadge variant="pill" /></div>

        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 text-center">
          {status === "verifying" && (
            <>
              <Loader2 className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Verifying your new email…</h1>
              <p className="text-sm text-slate-500 mt-2">Hang tight — this only takes a second.</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="verify-email-success">
                Email updated
              </h1>
              <p className="text-sm text-slate-600 mt-2">Your GLPFlo sign-in email is now <strong>{newEmail}</strong>. Use it next time you log in.</p>
              <Link to="/login" className="inline-block mt-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6 py-3 text-sm font-semibold">Sign in</Link>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="verify-email-error">
                We couldn&apos;t verify this link
              </h1>
              <p className="text-sm text-slate-600 mt-2">{message || "This link may have expired or already been used. Request a new email change from your profile."}</p>
              <Link to="/profile" className="inline-block mt-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6 py-3 text-sm font-semibold">Back to profile</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
